// Audit helpers. The `AuditAction` enum covers DB row mutations (INSERT/UPDATE/DELETE)
// plus explicit auth / system events written by Hono routes (see plan Section 5).
//
// 2026-11-01 (Batch 7 Wave 3) — reconciled with the action strings ACTUALLY
// written by apps/api routes (verified against live audit_log: 20 distinct
// actions). Previously the enum drifted from reality: it carried
// "webhook.send"/"pwa.install"/"template.update" (kebab-case values that no
// route writes — the webhook routes write "WEBHOOK_SEND"/"WEBHOOK_FAIL")
// while missing LOGIN, LOGOUT, CLEAR, BULK_IMPORT, BACKUPS_DELETE,
// DELETE_FAIL, APPLY_SLOT_CONFIG and four of the ai.* events. The enum is
// reference-only (no route imports it); each key below lists its writer.
// "ai.schedule_chat" appears only as historical audit_log rows — no current
// route writes it (kept out of the enum).
//
// password-change vs password-force-change (2026-11-01): voluntary self-service
// changes (POST /auth/change-password, user already knew the current password)
// write "password-change"; "password-force-change" is reserved for a user
// clearing the mustChangePassword gate (the forced first-change after an admin
// reset). Existing audit rows are unaffected — new voluntary changes simply
// use the new string.
export const AuditAction = {
    INSERT: "INSERT",
    UPDATE: "UPDATE",
    DELETE: "DELETE",
    // Auth events (tableName='auth')
    PASSWORD_RESET_REQUESTED: "password-reset-requested",
    PASSWORD_RESET_FAILED: "password-reset-failed",
    PASSWORD_RESET_SUCCESS: "password-reset-success",
    ADMIN_RESET_PASSWORD: "admin-reset-password",
    PASSWORD_FORCE_CHANGE: "password-force-change",
    PASSWORD_CHANGE: "password-change",
    LOGIN: "LOGIN", // POST /auth/login (post-login sweep)
    LOGOUT: "LOGOUT", // POST /auth/logout
    // AI events (routes/ai.ts + routes/blockReschedule.ts)
    AI_TEST: "ai.test",
    AI_FAIL: "ai.fail",
    AI_ICD10: "ai.icd10",
    AI_TRANSCRIBE: "ai.transcribe",
    AI_TRANSCRIBE_TEST: "ai.transcribe_test",
    AI_SUGGEST_ASSIGNMENTS: "ai.suggest_assignments",
    AI_BLOCK_RESCHEDULE: "ai.block_reschedule",
    // Webhook events (routes/webhook.ts, referralLetters.ts, medicalCertificates.ts)
    WEBHOOK_SEND: "WEBHOOK_SEND",
    WEBHOOK_FAIL: "WEBHOOK_FAIL",
    // Admin / system events
    BULK_IMPORT: "BULK_IMPORT", // routes/drugs.ts, routes/investigations.ts
    BACKUPS_DELETE: "BACKUPS_DELETE", // routes/backups.ts
    CLEAR: "CLEAR", // routes/errorLog.ts (manual error-log purge)
    DELETE_FAIL: "DELETE_FAIL", // routes/admin/users.ts (Appwrite/DB delete half-failed)
    APPLY_SLOT_CONFIG: "APPLY_SLOT_CONFIG", // routes/slotConfigs.ts (apply a stored config)
};
/**
 * Field-name-only diff (default — no PHI payloads). Returns the list of changed field names.
 * Use `diffVerbose` when a route opts in to capturing full values (e.g. non-PHI config).
 */
export function diff(before, after) {
    const beforeKeys = before ? Object.keys(before) : [];
    const afterKeys = after ? Object.keys(after) : [];
    const allKeys = new Set([...beforeKeys, ...afterKeys]);
    const changed = [];
    for (const k of allKeys) {
        const b = before?.[k];
        const a = after?.[k];
        if (!Object.is(b, a) && JSON.stringify(b) !== JSON.stringify(a)) {
            changed.push(k);
        }
    }
    return changed;
}
/** Full-field diff with before/after values — opt-in for non-PHI routes. */
export function diffVerbose(before, after) {
    const beforeKeys = before ? Object.keys(before) : [];
    const afterKeys = after ? Object.keys(after) : [];
    const allKeys = new Set([...beforeKeys, ...afterKeys]);
    const entries = [];
    for (const k of allKeys) {
        const b = before?.[k];
        const a = after?.[k];
        if (!Object.is(b, a) && JSON.stringify(b) !== JSON.stringify(a)) {
            entries.push({ field: k, before: b, after: a });
        }
    }
    return entries;
}
//# sourceMappingURL=audit.js.map